import React, { useMemo } from "react";
import TodayRow from "./TodayRow";
import AvatarChip from "./ui/avatar-chip";

/**
 * UnifiedTodayList — merges priorities + overdue tasks + today tasks + jots
 * (Phase B) + urgent briefs into a single flat ordered list.
 *
 * Props:
 *   priorities        [{ idx, text, completed, assignee_name, linked_task_id }]
 *   tasks             [{ task_id, title, status, due_date, priority, assignee_name, lead_name, brief_title, ... }]
 *   briefsDueSoon     [{ brief_id, title, status, pitch_date, company_name, assignee_name }]
 *   jots              [{ jot_id, text, done }]   — Phase B, empty for now
 *   onNavigate(path)  router push
 *   onToggleTask(id, nextDone)
 *   onToggleBrief(id) — optional, briefs have no checkbox
 *   onTogglePriority(idx, nextCompleted)
 *   onToggleJot(id, nextDone)
 *   todayStr          YYYY-MM-DD
 */
export default function UnifiedTodayList({
  priorities = [],
  tasks = [],
  briefsDueSoon = [],
  jots = [],
  onNavigate,
  onToggleTask,
  onTogglePriority,
  onToggleJot,
  onPromoteJot,
  onDeleteJot,
  onDeleteTask,
  onReorderJots,        // (orderedJotIds: string[]) => void
  onEditJotText,        // (jot_id, newText) => Promise
  onEditTaskTitle,      // (task_id, newTitle) => Promise
  todayStr,
}) {
  // Drag state for reordering jots within today's list
  const [dragJotId, setDragJotId] = React.useState(null);
  const [dragOverJotId, setDragOverJotId] = React.useState(null);

  const orderedJotIds = React.useMemo(
    () => (jots || []).filter((j) => !j.done).map((j) => j.jot_id),
    [jots]
  );

  const onJotDragStart = (jotId) => (e) => {
    setDragJotId(jotId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", jotId);
  };
  const onJotDragOver = (jotId) => (e) => { e.preventDefault(); setDragOverJotId(jotId); };
  const onJotDrop = (jotId) => (e) => {
    e.preventDefault();
    const from = dragJotId;
    setDragJotId(null); setDragOverJotId(null);
    if (!from || from === jotId || !onReorderJots) return;
    const fromIdx = orderedJotIds.indexOf(from);
    const toIdx = orderedJotIds.indexOf(jotId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...orderedJotIds];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onReorderJots(next);
  };
  const onJotDragEnd = () => { setDragJotId(null); setDragOverJotId(null); };
  const { activeItems, doneItems, totalCount } = useMemo(() => {
    const active = [];
    const done = [];

    // 1) Priorities (pinned, order preserved, 3 slots max)
    (priorities || []).forEach((p, idx) => {
      if (!p || !p.text) return;
      const item = {
        kind: "priority",
        key: `pri-${idx}`,
        text: p.text,
        done: !!p.completed,
        idx,
        meta: p.assignee_name,
        onToggle: (next) => onTogglePriority && onTogglePriority(idx, next),
        onClick: p.linked_task_id ? () => onNavigate?.(`/tasks/${p.linked_task_id}`) : undefined,
      };
      (p.completed ? done : active).push(item);
    });

    // 2) Overdue tasks + 3) Today tasks (dedupe against priorities by linked_task_id)
    const prioritizedIds = new Set((priorities || []).map((p) => p?.linked_task_id).filter(Boolean));
    const activeTaskFilter = (t) => {
      if (!t || !t.task_id) return false;
      if (prioritizedIds.has(t.task_id)) return false;
      if (t.status === "Done" || t.status === "Completed") return false;
      return true;
    };

    const overdueTasks = (tasks || []).filter((t) => {
      if (!activeTaskFilter(t)) return false;
      if (!t.due_date) return false;
      return t.due_date.slice(0, 10) < todayStr;
    });
    const todayTasks = (tasks || []).filter((t) => {
      if (!activeTaskFilter(t)) return false;
      if (!t.due_date) return false;
      return t.due_date.slice(0, 10) === todayStr;
    });

    // 2a) Jots FIRST — per user feedback, newest capture belongs at the top.
    //     Draggable for reorder. Text is inline-editable (click title).
    (jots || []).forEach((j) => {
      const item = {
        kind: "jot",
        key: `jot-${j.jot_id}`,
        text: j.text,
        done: !!j.done,
        onToggle: (next) => onToggleJot && onToggleJot(j.jot_id, next),
        actions: [
          { label: "→ Task", onClick: () => onPromoteJot && onPromoteJot(j, "task") },
          { label: "→ Brief", onClick: () => onPromoteJot && onPromoteJot(j, "brief") },
          { label: "Delete", destructive: true, onClick: () => onDeleteJot && onDeleteJot(j.jot_id) },
        ],
        swipeRight: !j.done ? { action: () => onToggleJot && onToggleJot(j.jot_id, true), hint: "✓ Done" } : undefined,
        swipeLeft: { action: () => onDeleteJot && onDeleteJot(j.jot_id), hint: "🗑 Delete" },
        // Drag + inline edit
        draggable: !j.done && !!onReorderJots,
        dragId: j.jot_id,
        onSaveText: onEditJotText ? (newText) => onEditJotText(j.jot_id, newText) : undefined,
      };
      (j.done ? done : active).push(item);
    });

    // 2b) Overdue tasks — WITH hover actions per user feedback. Title click-to-edit.
    overdueTasks.forEach((t) => {
      active.push({
        kind: "task",
        key: `task-${t.task_id}`,
        text: t.title || "Untitled task",
        done: false,
        meta: { flavor: "overdue", due_date: t.due_date, assignee_name: t.assignee_name, context: t.brief_title || t.lead_name || t.project_name },
        onToggle: (next) => onToggleTask && onToggleTask(t.task_id, next),
        onClick: () => onNavigate?.(`/tasks/${t.task_id}`),
        actions: [
          { label: "Open", onClick: () => onNavigate?.(`/tasks/${t.task_id}`) },
          ...(onDeleteTask ? [{ label: "Delete", destructive: true, onClick: () => onDeleteTask(t.task_id, t) }] : []),
        ],
        onSaveText: onEditTaskTitle ? (newTitle) => onEditTaskTitle(t.task_id, newTitle) : undefined,
      });
    });

    // 2c) Today tasks — same hover treatment + inline title edit
    todayTasks.forEach((t) => {
      active.push({
        kind: "task",
        key: `task-${t.task_id}`,
        text: t.title || "Untitled task",
        done: false,
        meta: { flavor: "today", due_date: t.due_date, due_time: t.due_time, assignee_name: t.assignee_name, context: t.brief_title || t.lead_name || t.project_name },
        onToggle: (next) => onToggleTask && onToggleTask(t.task_id, next),
        onClick: () => onNavigate?.(`/tasks/${t.task_id}`),
        actions: [
          { label: "Open", onClick: () => onNavigate?.(`/tasks/${t.task_id}`) },
          ...(onDeleteTask ? [{ label: "Delete", destructive: true, onClick: () => onDeleteTask(t.task_id, t) }] : []),
        ],
        onSaveText: onEditTaskTitle ? (newTitle) => onEditTaskTitle(t.task_id, newTitle) : undefined,
      });
    });

    // 5) Briefs with pitch today/tomorrow surfaced in Today
    (briefsDueSoon || []).forEach((b) => {
      active.push({
        kind: "brief",
        key: `brief-${b.brief_id}`,
        text: b.title || b.company_name || "Untitled brief",
        done: false,
        meta: { flavor: "brief", status: b.status, company_name: b.company_name, pitch_date: b.pitch_date },
        rightBadge: b.pitch_date ? (b.pitch_date.slice(0, 10) === todayStr ? "Pitch today" : "Pitch tomorrow") : undefined,
        onClick: () => onNavigate?.(`/bizdev/briefs/${b.brief_id}`),
      });
    });

    return { activeItems: active, doneItems: done, totalCount: active.length };
  }, [priorities, tasks, briefsDueSoon, jots, todayStr, onNavigate, onToggleTask, onTogglePriority, onToggleJot, onPromoteJot, onDeleteJot, onDeleteTask, onReorderJots, onEditJotText, onEditTaskTitle]);

  const renderMeta = (meta) => {
    if (!meta) return null;
    if (typeof meta === "string") return <span>{meta}</span>;
    if (meta.flavor === "overdue") {
      const days = daysDiff(meta.due_date, todayStr);
      return (
        <>
          <TodayRow.Overdue>
            {days > 1 ? `Overdue by ${days} days` : "Overdue"}
          </TodayRow.Overdue>
          {meta.assignee_name && <><TodayRow.Sep /><AvatarChip name={meta.assignee_name} /><span>{meta.assignee_name}</span></>}
          {meta.context && <><TodayRow.Sep /><span>{meta.context}</span></>}
        </>
      );
    }
    if (meta.flavor === "today") {
      return (
        <>
          <TodayRow.Today>
            Today{meta.due_time ? ` · ${formatTime(meta.due_time)}` : ""}
          </TodayRow.Today>
          {meta.assignee_name && <><TodayRow.Sep /><AvatarChip name={meta.assignee_name} /><span>{meta.assignee_name}</span></>}
          {meta.context && <><TodayRow.Sep /><span>{meta.context}</span></>}
        </>
      );
    }
    if (meta.flavor === "brief") {
      return (
        <>
          {meta.status && <TodayRow.Status status={meta.status} />}
          {meta.company_name && <><TodayRow.Sep /><span>{meta.company_name}</span></>}
        </>
      );
    }
    return <span>{String(meta)}</span>;
  };

  if (totalCount === 0 && doneItems.length === 0) {
    return (
      <div
        style={{
          padding: "32px 12px",
          textAlign: "center",
          color: "var(--t3)",
          fontSize: 14,
          background: "var(--s1)",
          border: "1px solid var(--bdr)",
          borderRadius: 12,
        }}
      >
        <div style={{ fontSize: 20, marginBottom: 6 }}>✓</div>
        <div style={{ color: "var(--g)", fontWeight: 600, marginBottom: 4 }}>All clear</div>
        <div>Nothing on your plate today. Add a priority or jot above.</div>
      </div>
    );
  }

  return (
    <div>
      {activeItems.map((item) => (
        <TodayRow
          key={item.key}
          kind={item.kind}
          text={item.text}
          done={item.done}
          onToggleDone={item.onToggle}
          onClick={item.onClick}
          metadata={renderMeta(item.meta)}
          rightBadge={item.rightBadge}
          actions={item.actions || []}
          swipeRight={item.swipeRight}
          swipeLeft={item.swipeLeft}
          draggable={item.draggable}
          onDragStart={item.dragId ? onJotDragStart(item.dragId) : undefined}
          onDragOver={item.dragId ? onJotDragOver(item.dragId) : undefined}
          onDrop={item.dragId ? onJotDrop(item.dragId) : undefined}
          onDragEnd={item.dragId ? onJotDragEnd : undefined}
          isDragging={item.dragId === dragJotId}
          isDragTarget={item.dragId === dragOverJotId && dragJotId !== null && dragJotId !== item.dragId}
          onSaveText={item.onSaveText}
        />
      ))}
      {doneItems.length > 0 && (
        <>
          <div style={{
            margin: "20px 0 10px",
            height: 1,
            background: "linear-gradient(to right, transparent, var(--bdr) 20%, var(--bdr) 80%, transparent)",
          }} />
          <div style={{
            fontSize: 12,
            color: "var(--t3)",
            padding: "0 12px 6px",
            fontWeight: 500,
          }}>
            Done today · {doneItems.length}
          </div>
          {doneItems.map((item) => (
            <TodayRow
              key={item.key}
              kind={item.kind}
              text={item.text}
              done
              onToggleDone={item.onToggle}
              onClick={item.onClick}
              metadata={renderMeta(item.meta)}
            />
          ))}
        </>
      )}
    </div>
  );
}

function daysDiff(dateStr, todayStr) {
  if (!dateStr) return 0;
  try {
    const d = new Date((dateStr || "").slice(0, 10) + "T00:00:00");
    const t = new Date(todayStr + "T00:00:00");
    return Math.max(1, Math.round((t - d) / 86400000));
  } catch { return 1; }
}

function formatTime(s) {
  if (!s) return "";
  try {
    const [h, m] = s.split(":");
    const hr = parseInt(h);
    const ampm = hr >= 12 ? "PM" : "AM";
    const hr12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
    return `${hr12}:${m} ${ampm}`;
  } catch { return s; }
}
